import { useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '';

interface SettingsPanelProps {
  onClose: () => void;
  token: string;
}

export default function SettingsPanel({ onClose, token }: SettingsPanelProps) {
  const [offlineTimeout, setOfflineTimeout] = useState('90');
  const [printsThreshold, setPrintsThreshold] = useState('20');
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/config`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json() as Promise<Record<string, string>>)
      .then((cfg) => {
        setOfflineTimeout(cfg.offline_timeout_seconds ?? '90');
        setPrintsThreshold(cfg.prints_remaining_threshold ?? '20');
        setEmailEnabled(cfg.email_enabled === 'true');
        setEmailRecipients(cfg.email_recipients ?? '');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          offline_timeout_seconds: offlineTimeout,
          prints_remaining_threshold: printsThreshold,
          email_enabled: String(emailEnabled),
          email_recipients: emailRecipients,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="settings-backdrop fixed inset-0 z-30"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 z-40 w-full max-w-sm bg-white border-l border-surface-border shadow-xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-brand-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="font-raleway font-thin text-sm uppercase tracking-[0.15em] text-ink-primary">Einstellungen</h2>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg bg-surface-subtle border border-surface-border flex items-center justify-center text-ink-muted hover:text-ink-primary hover:border-surface-border-dark transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading ? (
            <p className="text-ink-muted text-sm font-mono animate-pulse">Lade Konfiguration…</p>
          ) : (
            <>
              {/* Section: Schwellwerte */}
              <section className="space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-ink-muted border-b border-surface-border pb-2">
                  Schwellwerte
                </h3>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-ink-secondary">
                    Offline-Schwellwert
                  </label>
                  <p className="text-xs text-ink-muted">
                    Fotobox gilt als offline nach X Sekunden ohne Heartbeat.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="10"
                      max="600"
                      value={offlineTimeout}
                      onChange={(e) => setOfflineTimeout(e.target.value)}
                      className="w-24 bg-surface-subtle border border-surface-border rounded-lg px-3 py-1.5 font-mono text-sm text-ink-primary focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors"
                    />
                    <span className="text-ink-muted text-sm">Sekunden</span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-ink-secondary">
                    Druckbestand-Alarm
                  </label>
                  <p className="text-xs text-ink-muted">
                    Alarm auslösen wenn verbleibende Drucke unter diesen Wert fallen.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="500"
                      value={printsThreshold}
                      onChange={(e) => setPrintsThreshold(e.target.value)}
                      className="w-24 bg-surface-subtle border border-surface-border rounded-lg px-3 py-1.5 font-mono text-sm text-ink-primary focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors"
                    />
                    <span className="text-ink-muted text-sm">Drucke</span>
                  </div>
                </div>
              </section>

              {/* Section: E-Mail */}
              <section className="space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-ink-muted border-b border-surface-border pb-2">
                  E-Mail Benachrichtigungen
                </h3>

                <label className="flex items-center gap-3 cursor-pointer">
                  <div
                    onClick={() => setEmailEnabled((v) => !v)}
                    className={[
                      'relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 cursor-pointer',
                      emailEnabled ? 'bg-brand-green' : 'bg-surface-border-dark',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200',
                        emailEnabled ? 'translate-x-5' : 'translate-x-0',
                      ].join(' ')}
                    />
                  </div>
                  <span className="text-sm font-medium text-ink-secondary">
                    {emailEnabled ? 'Aktiviert' : 'Deaktiviert'}
                  </span>
                </label>

                {emailEnabled && (
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-ink-secondary">
                      Empfänger (kommagetrennt)
                    </label>
                    <textarea
                      rows={3}
                      value={emailRecipients}
                      onChange={(e) => setEmailRecipients(e.target.value)}
                      placeholder="admin@example.com, technik@example.com"
                      className="w-full bg-surface-subtle border border-surface-border rounded-lg px-3 py-2 font-mono text-xs text-ink-primary focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors resize-none placeholder:text-ink-faint"
                    />
                    <p className="text-xs text-ink-muted">
                      SMTP-Zugangsdaten werden in der <code className="text-brand-green font-mono">.env</code>-Datei konfiguriert.
                    </p>
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-surface-border">
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className={[
              'w-full py-2.5 rounded-xl font-exo font-semibold text-sm transition-all duration-200',
              saved
                ? 'bg-brand-green text-white'
                : 'bg-brand-green hover:bg-brand-green/90 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-sm',
            ].join(' ')}
          >
            {saving ? 'Wird gespeichert…' : saved ? '✓ Gespeichert' : 'Einstellungen speichern'}
          </button>
        </div>
      </div>
    </>
  );
}
